/**
 * Holding ground (Phase 66.5, decision 119): **sectors of fire**. A squad that holds a point watches every
 * way instead of everyone looking at one thing: its living members share the circle, each facing its own
 * sector, counted round from the leader's heading in formation order. A bot already facing a threat's
 * direction engages it sooner (its turn rate is the limit), so a squad with sectors answers an attack from
 * any side. **Regrouping after losses** needs no new rule: the formation order is the living members, so
 * slots close up and the senior survivor leads when the leader falls, a respawned member rejoins its
 * squad's base order and slot, and the adaptive commander sends a thinned squad to escort a full one.
 * Pure; deterministic math only.
 */
import type { GameState, MatchSquad, PlayerState, Vec3 } from '../types';
import { vecDot, vecNormalize } from '../math';
import { dacos } from './dmath';
import { headingOf, projectToTangent, turnHeading } from './surfaceWalk';
import { formationOrder } from './formation';

export type HoldingSquad = Pick<MatchSquad, 'unitIds' | 'formation'>;

/**
 * The direction a member faces while its squad holds: the leader's heading turned by its share of the
 * circle (member i of n faces i × 360° / n round), in the member's own ground plane. Null when the member
 * is not among the squad's living or stands alone (one member watches where it likes).
 */
export function sectorFacing(state: Pick<GameState, 'players'>, squad: HoldingSquad, unitId: number): Vec3 | null {
  const order = formationOrder(state, squad);
  if (order.length < 2) return null;
  const idx = order.findIndex(p => p.id === unitId);
  if (idx < 0) return null;
  const me = order[idx];
  const n = vecNormalize(me.surfaceDir);
  const base = projectToTangent(headingOf(order[0]), n);
  return turnHeading(base, n, (idx * 2 * Math.PI) / order.length);
}

/** The mean angle (radians) between the headings of every pair of a squad's living members; 0 with fewer than two. */
export function facingSpread(state: Pick<GameState, 'players'>, squad: HoldingSquad): number {
  const order = formationOrder(state, squad);
  if (order.length < 2) return 0;
  const h = order.map(p => headingOf(p));
  let sum = 0, n = 0;
  for (let i = 0; i < h.length; i++) for (let j = i + 1; j < h.length; j++) {
    sum += dacos(Math.max(-1, Math.min(1, vecDot(h[i], h[j]))));
    n++;
  }
  return n ? sum / n : 0;
}

/** Living members of a squad, in formation order (the leader first). */
export function holdingMembers(state: Pick<GameState, 'players'>, squad: HoldingSquad): PlayerState[] {
  return formationOrder(state, squad);
}
